import React, { useState, useEffect } from 'react';
import { Button, Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Textarea, Select, SelectItem, Spinner, Checkbox } from '@nextui-org/react';
import { FiPlus, FiEdit2, FiTrash2, FiX, FiSearch, FiFilter } from 'react-icons/fi';
import { 
  getNotesList, 
  createNote, 
  updateNote, 
  deleteNote,
  deleteMultipleNotes,
  NOTE_PRIORITIES,
  NOTE_STATUS,
  getPriorityLabel,
  getPriorityColor,
  getStatusLabel,
  getStatusColor,
  sortNotesByPriority,
  filterNotesByStatus,
  filterNotesByPriority,
  searchNotes
} from '../../../services/notes';

const StickyNotesComponent = ({ projectId }) => {
  const [notes, setNotes] = useState([]);
  const [filteredNotes, setFilteredNotes] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);
  const [editingNote, setEditingNote] = useState(null);
  const [noteSubject, setNoteSubject] = useState('');
  const [noteContent, setNoteContent] = useState('');
  const [notePriority, setNotePriority] = useState(NOTE_PRIORITIES.MEDIUM);
  const [noteColor, setNoteColor] = useState('yellow');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedNotes, setSelectedNotes] = useState([]);

  // Tüm renk seçenekleri
  const allColors = [
    { name: 'green', bg: 'bg-green-200', border: 'border-green-300', text: 'text-green-900' },
    { name: 'yellow', bg: 'bg-yellow-200', border: 'border-yellow-300', text: 'text-yellow-900' },
    { name: 'blue', bg: 'bg-blue-200', border: 'border-blue-300', text: 'text-blue-900' },
    { name: 'orange', bg: 'bg-orange-200', border: 'border-orange-300', text: 'text-orange-900' },
    { name: 'red', bg: 'bg-red-200', border: 'border-red-300', text: 'text-red-900' },
  ];

  // Renk seçimine göre priority belirleme
  const getPriorityFromColor = (color) => {
    const colorToPriority = {
      'green': 1,        // 1 - Yeşil
      'yellow': 2,       // 2 - Sarı
      'blue': 3,         // 3 - Mavi
      'orange': 4,       // 4 - Turuncu
      'red': 5,          // 5 - Kırmızı
      'pink': 5,         // 5 - Kırmızı (pembe de kırmızı olarak)
      'purple': 5        // 5 - Kırmızı (mor da kırmızı olarak)
    };
    return colorToPriority[color] || 2; // Varsayılan sarı
  };



  // Dark mode renkleri
  const darkColors = [
    { name: 'green', bg: 'dark:bg-green-900/30', border: 'dark:border-green-700', text: 'dark:text-green-100' },
    { name: 'yellow', bg: 'dark:bg-yellow-900/30', border: 'dark:border-yellow-700', text: 'dark:text-yellow-100' },
    { name: 'blue', bg: 'dark:bg-blue-900/30', border: 'dark:border-blue-700', text: 'dark:text-blue-100' },
    { name: 'orange', bg: 'dark:bg-orange-900/30', border: 'dark:border-orange-700', text: 'dark:text-orange-100' },
    { name: 'red', bg: 'dark:bg-red-900/30', border: 'dark:border-red-700', text: 'dark:text-red-100' },
  ];

  // API'den notları yükle
  useEffect(() => {
    fetchNotes();
  }, []);

  // Filtreleme ve arama
  useEffect(() => {
    let filtered = [...notes];

    // Arama
    if (searchTerm) {
      filtered = searchNotes(filtered, searchTerm);
    }

    setFilteredNotes(filtered);
  }, [notes, searchTerm]);

  // API'den notları getir
  const fetchNotes = async () => {
    try {
      setLoading(true);
      setError(null);
      const notesData = await getNotesList();

      setNotes(notesData);
    } catch (error) {
      console.error('Notlar yüklenirken hata:', error);
      setError('Notlar yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  // Yeni not ekle
  const handleAddNote = () => {
    setEditingNote(null);
    setNoteSubject('');
    setNoteContent('');
    setNoteColor('yellow');
    setNotePriority(getPriorityFromColor('yellow')); // Renk seçimine göre priority
    setIsModalOpen(true);
  };

  // Not düzenle
  const handleEditNote = (note) => {
    setEditingNote(note);
    setNoteSubject(note.subject);
    setNoteContent(note.content);
    setNoteColor(note.color || 'yellow');
    setNotePriority(getPriorityFromColor(note.color || 'yellow')); // Renk seçimine göre priority
    setIsModalOpen(true);
  };

  // Not kaydet
  const handleSaveNote = async () => {
    if (!noteSubject.trim() || !noteContent.trim()) return;

    try {
      setLoading(true);
      const noteData = {
        subject: noteSubject.trim(),
        content: noteContent.trim(),
        priority: notePriority,
        color: noteColor
      };


      if (editingNote) {
        await updateNote(editingNote.id, noteData);
      } else {
        await createNote(noteData);
      }

      await fetchNotes(); // Notları yeniden yükle
      setIsModalOpen(false);
      setNoteSubject('');
      setNoteContent('');
      setEditingNote(null);
    } catch (error) {
      console.error('Not kaydedilirken hata:', error);
      setError('Not kaydedilirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  // Not silme modal'ını aç
  const openDeleteModal = (note) => {
    setNoteToDelete(note);
    setIsDeleteModalOpen(true);
  };

  // Not sil
  const handleDeleteNote = async () => {
    if (!noteToDelete) return;
    
    try {
      setLoading(true);
      await deleteNote(noteToDelete.id);
      await fetchNotes(); // Notları yeniden yükle
      setIsDeleteModalOpen(false);
      setNoteToDelete(null);
    } catch (error) {
      console.error('Not silinirken hata:', error);
      setError('Not silinirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  // Not seçme/seçimi kaldırma
  const toggleNoteSelection = (noteId) => {
    setSelectedNotes(prev => {
      if (prev.includes(noteId)) {
        return prev.filter(id => id !== noteId);
      } else {
        return [...prev, noteId];
      }
    });
  };

  // Tümünü seç/seçimi kaldır
  const toggleSelectAll = () => {
    if (selectedNotes.length === filteredNotes.length) {
      setSelectedNotes([]);
    } else {
      setSelectedNotes(filteredNotes.map(note => note.id));
    }
  };

  // Toplu silme modal'ını aç
  const openBulkDeleteModal = () => {
    if (selectedNotes.length === 0) return;
    setIsBulkDeleteModalOpen(true);
  };

  // Toplu silme
  const handleBulkDelete = async () => {
    if (selectedNotes.length === 0) return;
    
    try {
      setLoading(true);
      await deleteMultipleNotes(selectedNotes);
      await fetchNotes(); // Notları yeniden yükle
      setSelectedNotes([]);
      setIsBulkDeleteModalOpen(false);
    } catch (error) {
      console.error('Notlar silinirken hata:', error);
      setError('Notlar silinirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  // Modal kapat
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setNoteSubject('');
    setNoteContent('');
    setEditingNote(null);
  };

  // Renk seçimi
  const getColorClasses = (colorName) => {
    const color = allColors.find(c => c.name === colorName);
    const darkColor = darkColors.find(c => c.name === colorName);
    return `${color.bg} ${color.border} ${color.text} ${darkColor.bg} ${darkColor.border} ${darkColor.text}`;
  };

  // Loading state
  if (loading && notes.length === 0) {
    return (
      <div className="p-6 flex justify-center items-center min-h-[400px]">
        <div className="text-center">
          <Spinner size="lg" color="primary" />
          <p className="mt-4 text-gray-600 dark:text-gray-400">Notlar yükleniyor...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="p-6 text-center">
        <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
        <Button
          onClick={fetchNotes}
          className="bg-[#da402f] dark:bg-[#7375E1] text-white"
        >
          Tekrar Dene
        </Button>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-6 gap-4">
        <div className="flex-1">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Notlarım
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            {filteredNotes.length} not bulundu
            {selectedNotes.length > 0 && (
              <span className="ml-2 text-[#da402f] dark:text-[#7375E1] font-semibold">
                ({selectedNotes.length} seçili)
              </span>
            )}
          </p>
        </div>
        <div className="flex gap-2">
          {selectedNotes.length > 0 && (
            <>
              <Button
                onClick={toggleSelectAll}
                variant="bordered"
                className="border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300"
              >
                {selectedNotes.length === filteredNotes.length ? 'Seçimi Kaldır' : 'Tümünü Seç'}
              </Button>
              <Button
                onClick={openBulkDeleteModal}
                className="bg-red-600 dark:bg-red-700 text-white hover:bg-red-700 dark:hover:bg-red-800"
                startContent={<FiTrash2 size={18} />}
              >
                Seçilenleri Sil ({selectedNotes.length})
              </Button>
            </>
          )}
          <Button
            onClick={handleAddNote}
            className="bg-[#da402f] dark:bg-[#7375E1] text-white font-semibold px-6 py-3 rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 hover:bg-[#c73a2a] dark:hover:bg-[#6b5fd1]"
            startContent={<FiPlus size={20} />}
          >
            Not Ekle
          </Button>
        </div>
      </div>

      {/* Arama */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Notlarda ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            aria-label="Notlarda arama"
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-dark-300 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#da402f] dark:focus:ring-[#7375E1] focus:border-transparent"
          />
        </div>
      </div>

      {/* Notes Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {filteredNotes.map((note) => {

          const isSelected = selectedNotes.includes(note.id);
          return (
          <div
            key={note.id}
            className={`${getColorClasses(note.color || 'yellow')} border-2 rounded-lg p-4 shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105 relative group ${
              isSelected ? 'ring-4 ring-[#da402f] dark:ring-[#7375E1]' : ''
            }`}
          >
            {/* Checkbox - Sol Üst Köşe */}
            <div className="absolute top-2 left-2 z-10">
              <Checkbox
                isSelected={isSelected}
                onValueChange={() => toggleNoteSelection(note.id)}
                classNames={{
                  base: "transition-all duration-200",
                  wrapper: "bg-white dark:bg-gray-700 border-2 border-gray-300 dark:border-gray-500 shadow-sm hover:shadow-md after:bg-[#da402f] dark:after:bg-[#7375E1]",
                  icon: "text-white",
                  label: "text-gray-900 dark:text-white"
                }}
                color="default"
                size="lg"
                radius="sm"
              />
            </div>

            {/* Not Başlığı */}
            <div className="mb-2 mt-8">
              <h3 className="font-semibold text-sm leading-tight">
                {note.subject}
              </h3>
            </div>

            {/* Not İçeriği */}
            <div className="min-h-[100px] flex flex-col">
              <p className="text-sm leading-relaxed flex-1 whitespace-pre-wrap">
                {note.content}
              </p>
            </div>

            {/* Aksiyon Butonları */}
            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
              <div className="flex space-x-1">
                <button
                  onClick={() => handleEditNote(note)}
                  className="p-1 bg-white/80 dark:bg-gray-800/80 rounded-full hover:bg-white dark:hover:bg-gray-700 transition-colors"
                  title="Düzenle"
                >
                  <FiEdit2 size={14} />
                </button>
                <button
                  onClick={() => openDeleteModal(note)}
                  className="p-1 bg-white/80 dark:bg-gray-800/80 rounded-full hover:bg-red-100 dark:hover:bg-red-900/50 transition-colors"
                  title="Sil"
                >
                  <FiTrash2 size={14} className="text-red-600 dark:text-red-400" />
                </button>
              </div>
            </div>
          </div>
          );
        })}
      </div>

      {/* Boş Durum */}
      {filteredNotes.length === 0 && !loading && (
        <div className="text-center py-12">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            {notes.length === 0 ? 'Henüz not yok' : 'Filtrelere uygun not bulunamadı'}
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {notes.length === 0 ? 'İlk notunuzu ekleyerek başlayın' : 'Farklı filtreler deneyin'}
          </p>
          <Button
            onClick={handleAddNote}
            className="bg-[#da402f] dark:bg-[#7375E1] text-white hover:bg-[#c73a2a] dark:hover:bg-[#6b5fd1]"
            startContent={<FiPlus size={16} />}
          >
            {notes.length === 0 ? 'İlk Notu Ekle' : 'Yeni Not Ekle'}
          </Button>
        </div>
      )}

      {/* Not Ekleme/Düzenleme Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        size="md"
        hideCloseButton={true}
        classNames={{
          base: "bg-white dark:bg-dark-300",
          header: "border-b border-gray-200 dark:border-gray-600",
          body: "py-6",
          footer: "border-t border-gray-200 dark:border-gray-600"
        }}
      >
        <ModalContent>
          <ModalHeader className="flex flex-col gap-1">
            <div className="flex items-center justify-between w-full">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {editingNote ? 'Notu Düzenle' : 'Yeni Not Ekle'}
              </h3>
              <Button
                isIconOnly
                variant="light"
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <FiX size={20} />
              </Button>
            </div>
          </ModalHeader>
          
          <ModalBody>
            <div className="space-y-4">
              {/* Not Başlığı */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Not Başlığı *
                </label>
                <input
                  type="text"
                  placeholder="Not başlığını girin..."
                  value={noteSubject}
                  onChange={(e) => setNoteSubject(e.target.value)}
                  aria-label="Not başlığı"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-dark-300 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#da402f] dark:focus:ring-[#7375E1] focus:border-transparent"
                />
              </div>

              {/* Renk Seçimi */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Renk Seçin
                </label>
                <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  Renk seçimi otomatik olarak öncelik belirler
                </div>
                <div className="flex space-x-2">
                  {allColors.map((color) => (
                    <button
                      key={color.name}
                      onClick={() => {
                        setNoteColor(color.name);
                        setNotePriority(getPriorityFromColor(color.name)); // Renk seçimine göre priority güncelle
                      }}
                      className={`w-8 h-8 rounded-full ${color.bg} ${color.border} border-2 ${
                        noteColor === color.name ? 'ring-2 ring-gray-400 dark:ring-gray-500' : ''
                      } transition-all duration-200 hover:scale-110`}
                      title={`${color.name} - Priority: ${getPriorityFromColor(color.name)}`}
                    />
                  ))}
                </div>
              </div>

              {/* Not İçeriği */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Not İçeriği *
                </label>
                <Textarea
                  placeholder="Not içeriğini buraya yazın..."
                  value={noteContent}
                  onChange={(e) => setNoteContent(e.target.value)}
                  minRows={6}
                  maxRows={10}
                  aria-label="Not içeriği"
                  classNames={{
                    inputWrapper: "bg-white dark:bg-dark-300 border-gray-300 dark:border-gray-600",
                    input: "text-gray-900 dark:text-white",
                    placeholder: "text-gray-500 dark:text-gray-400"
                  }}
                />
              </div>
            </div>
          </ModalBody>
          
          <ModalFooter>
            <Button
              color="primary"
              onClick={handleSaveNote}
              disabled={!noteSubject.trim() || !noteContent.trim() || loading}
              className="bg-[#da402f] dark:bg-[#7375E1] text-white hover:bg-[#c73a2a] dark:hover:bg-[#6b5fd1] disabled:bg-gray-400 dark:disabled:bg-gray-600"
              startContent={loading ? <Spinner size="sm" color="white" /> : null}
            >
              {loading ? 'Kaydediliyor...' : (editingNote ? 'Güncelle' : 'Kaydet')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal 
        isOpen={isDeleteModalOpen} 
        onClose={() => setIsDeleteModalOpen(false)}
        size="md"
        classNames={{
          base: "bg-white dark:bg-dark-300",
          header: "border-b border-gray-200 dark:border-gray-600",
          body: "py-6",
          footer: "border-t border-gray-200 dark:border-gray-600"
        }}
      >
        <ModalContent>
          <ModalHeader className="flex flex-col gap-1">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                <FiTrash2 className="text-red-600 dark:text-red-400" size={20} />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Notu Sil
              </h3>
            </div>
          </ModalHeader>
          <ModalBody>
            <div className="text-center">
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Bu notu silmek istediğinizden emin misiniz?
              </p>
              {noteToDelete && (
                <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                  <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                    {noteToDelete.subject}
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-3">
                    {noteToDelete.content}
                  </p>
                </div>
              )}
              <p className="text-sm text-red-600 dark:text-red-400 mt-4">
                Bu işlem geri alınamaz.
              </p>
            </div>
          </ModalBody>
          <ModalFooter className="flex justify-end space-x-2">
            <Button 
              variant="light" 
              onPress={() => setIsDeleteModalOpen(false)}
              disabled={loading}
            >
              İptal
            </Button>
            <Button 
              color="danger"
              onPress={handleDeleteNote}
              isLoading={loading}
              startContent={loading ? null : <FiTrash2 size={16} />}
            >
              {loading ? 'Siliniyor...' : 'Sil'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Bulk Delete Confirmation Modal */}
      <Modal 
        isOpen={isBulkDeleteModalOpen} 
        onClose={() => setIsBulkDeleteModalOpen(false)}
        size="md"
        classNames={{
          base: "bg-white dark:bg-dark-300",
          header: "border-b border-gray-200 dark:border-gray-600",
          body: "py-6",
          footer: "border-t border-gray-200 dark:border-gray-600"
        }}
      >
        <ModalContent>
          <ModalHeader className="flex flex-col gap-1">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                <FiTrash2 className="text-red-600 dark:text-red-400" size={20} />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Toplu Not Silme
              </h3>
            </div>
          </ModalHeader>
          <ModalBody>
            <div className="text-center">
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                <span className="font-semibold text-[#da402f] dark:text-[#7375E1]">
                  {selectedNotes.length} adet
                </span> notu silmek istediğinizden emin misiniz?
              </p>
              <div className="max-h-48 overflow-y-auto space-y-2">
                {filteredNotes
                  .filter(note => selectedNotes.includes(note.id))
                  .map(note => (
                    <div 
                      key={note.id}
                      className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 text-left"
                    >
                      <h4 className="font-medium text-gray-900 dark:text-white text-sm">
                        {note.subject}
                      </h4>
                    </div>
                  ))
                }
              </div>
              <p className="text-sm text-red-600 dark:text-red-400 mt-4">
                Bu işlem geri alınamaz.
              </p>
            </div>
          </ModalBody>
          <ModalFooter className="flex justify-end space-x-2">
            <Button 
              variant="light" 
              onPress={() => setIsBulkDeleteModalOpen(false)}
              disabled={loading}
            >
              İptal
            </Button>
            <Button 
              color="danger"
              onPress={handleBulkDelete}
              isLoading={loading}
              startContent={loading ? null : <FiTrash2 size={16} />}
            >
              {loading ? 'Siliniyor...' : `${selectedNotes.length} Notu Sil`}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
};

export default StickyNotesComponent;
